import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ParkingProcessListItemDto, ParkingProcessDetectionLogDto } from '../../../../../api/gen';
import { ResponsiveBaseComponent, JJamesBadgeComponent, BadgeVariant } from 'shared-lib';
import { ParkingZoneParkingViolationDetailsComponent } from './parking-zone-parking-violation-details/parking-zone-parking-violation-details.component';

@Component({
  selector: 'app-parking-zone-parking-process-item',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatChipsModule,
    MatTooltipModule,
    JJamesBadgeComponent,
    ParkingZoneParkingViolationDetailsComponent
  ],
  templateUrl: './parking-zone-parking-process-item.component.html',
  styleUrl: './parking-zone-parking-process-item.component.scss'
})
export class ParkingZoneParkingProcessItemComponent extends ResponsiveBaseComponent {
  @Input({ required: true }) process!: ParkingProcessListItemDto;

  @Output() clicked = new EventEmitter<ParkingProcessListItemDto>();

  showViolationDetails = false;
  showDetectionLogs = false;

  onClick(): void {
    this.clicked.emit(this.process);
  }

  toggleViolationDetails(event: Event): void {
    event.stopPropagation();
    this.showViolationDetails = !this.showViolationDetails;
  }

  closeViolationDetails(): void {
    this.showViolationDetails = false;
  }

  toggleDetectionLogs(event: Event): void {
    event.stopPropagation();
    this.showDetectionLogs = !this.showDetectionLogs;
  }

  getDetectionLogs(): ParkingProcessDetectionLogDto[] {
    return this.process.detectionLogs || [];
  }

  hasDetectionLogs(): boolean {
    return (this.process.detectionLogs?.length || 0) > 0;
  }

  getDetectionLogCount(): number {
    return this.process.detectionLogs?.length || 0;
  }

  getDetectionTypeBadgeVariant(type: string | undefined): BadgeVariant {
    switch (type) {
      case 'Start': return 'success';
      case 'Stop': return 'secondary';
      case 'Update': return 'info';
      case 'ExitRefused': return 'warning';
      default: return 'secondary';
    }
  }

  // Image preview
  previewImageUrl: string | null = null;

  openPreview(url: string, event: Event): void {
    event.stopPropagation();
    this.previewImageUrl = url;
  }

  closePreview(): void {
    this.previewImageUrl = null;
  }

  getStatusBadgeVariant(): BadgeVariant {
    return this.process.isActive ? 'success' : 'secondary';
  }

  getStatusText(): string {
    return this.process.isActive ? 'Active' : 'Ended';
  }

  getDuration(): string {
    // Use the pre-calculated duration from the API
    if (this.process.duration) {
      return this.formatDuration(this.process.duration);
    }
    return 'N/A';
  }

  private formatDuration(duration: string): string {
    // Duration format from API is like "00:00:00" (HH:mm:ss)
    const parts = duration.split(':');
    if (parts.length >= 2) {
      const hours = parseInt(parts[0], 10);
      const minutes = parseInt(parts[1], 10);

      if (hours > 0) {
        return `${hours}h ${minutes}m`;
      }
      return `${minutes}m`;
    }
    return duration;
  }

  formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString();
  }

  getShortId(id: string | undefined): string {
    if (!id) return 'N/A';
    return id.substring(0, 8) + '...';
  }

  // Enriched data getters
  getLicensePlateNumber(): string {
    return this.process.licensePlateDetails?.fullNumber || 'N/A';
  }

  getParkerName(): string {
    return this.process.parkerDetails?.displayName || 'N/A';
  }

  getParkerEmail(): string {
    return this.process.parkerDetails?.email || '';
  }

  getParkingRightType(): string {
    return this.process.parkingRightDetails?.parkingRightType || 'N/A';
  }

  getStrategyName(): string {
    return this.process.strategyDetails?.name || 'N/A';
  }
}
